import { debug, Debugger } from 'debug';
import { getSandboxEnvironment, root, ISandboxEnvironment, getServiceVersion } from '@estee/elc-universal-utils';
import * as Sentry from '@sentry/browser';

export interface IFullData {
    logdata: ILogData;
    level: string;
    config: IELCLogger;
}

export interface IPayload {
    error?: Error;
    context?: object;
    serviceRegistry?: object;
    serviceName?: string;
    serviceVersion?: string;
    siteUrl?: string;
}

export interface ILogData {
    triggerType: TriggerType;
    message: string | {
        error: Error
    } | Error;
    payload?: IPayload;
}

export enum TriggerType {
    runtimeError = 'runtimeError',
    api = 'api',
    graphqlApi = 'graphqlApi',
    render = 'render',
    serviceBus = 'serviceBus',
    config = 'config',
    translation = 'translation',
    other = 'other'
}

export interface IELCLogger {
    serviceName: string;
    serviceVersion?: string;
    environment?: string;
    buid?: string;
}

type ServicesLog = {
    [key: string]: {
        version: string;
        isStarted: boolean | null;
    }
};

export class ELCLogger {
    private config: IELCLogger;

    private errorDebugger?: Debugger;
    private infoDebugger?: Debugger;
    private warningDebugger?: Debugger;
    private eventDebugger?: Debugger;
    private queryDebugger?: Debugger;
    private commandDebugger?: Debugger;

    constructor(config: IELCLogger) {
        this.config = config;
        const logger = debug(this.config.serviceName);

        this.errorDebugger = logger.extend('error');
        this.infoDebugger = logger.extend('info');
        this.warningDebugger = logger.extend('warning');
        this.eventDebugger = logger.extend('event');
        this.queryDebugger = logger.extend('query');
        this.commandDebugger = logger.extend('command');
    }

    public error = async (logdata: ILogData) => {
        if (this.errorDebugger) {
            this.errorDebugger(this.enhanceLogData(logdata));
        }

        const isProd =
            this.config.environment === ISandboxEnvironment.PRODUCTION ||
            root.env === ISandboxEnvironment.PRODUCTION;

        if ((isProd && getSandboxEnvironment() === ISandboxEnvironment.LIVE) || !isProd) {
            return this.sendLog(logdata, 'error');
        }
    };

    public info = (logdata: ILogData) => {
        if (this.infoDebugger) {
            this.infoDebugger(this.enhanceLogData(logdata));
        }
    };

    public warning = (logdata: ILogData) => {
        if (this.warningDebugger) {
            this.warningDebugger(this.enhanceLogData(logdata));
        }
    };

    public event = (logdata: ILogData) => {
        if (this.eventDebugger) {
            this.eventDebugger(this.enhanceLogData(logdata));
        }
    };

    public query = (logdata: ILogData) => {
        if (this.queryDebugger) {
            this.queryDebugger(this.enhanceLogData(logdata));
        }
    };

    public command = (logdata: ILogData) => {
        if (this.commandDebugger) {
            this.commandDebugger(this.enhanceLogData(logdata));
        }
    };

    private enhanceLogData = (logdata: ILogData) => ({
        ...logdata,
        triggerSource: this.config.serviceName,
        date: new Date().toUTCString()
    });

    private getFSUrl = () => {
        try {
            // @ts-ignore
            const fsUrl = root.FS.getCurrentSessionURL();

            return fsUrl || '';
        } catch (e) {
            return '';
        }
    };

    private sendToSentry = (data: Event | Error | string, contextMsg = '') => {
        const isSentryAvailable = root.__SENTRY__ || root.Sentry;

        if (isSentryAvailable) {
            const fsUrl = this.getFSUrl();

            const serviceRegistryKeys = Object.keys(root.ServiceRegistry || {}).length;
            const serviceInfoKeys = Object.keys(root.serviceInfo || {}).length;
            const services: ServicesLog = {};

            if (serviceRegistryKeys <= serviceInfoKeys) {
                Object.entries(root.serviceInfo || {}).forEach(([key, value]) => {
                    const registry = root.ServiceRegistry || {};
                    const serviceRegistry = registry[key];

                    services[key] = {
                        isStarted: serviceRegistry.isStarted || null,
                        version: value.version || getServiceVersion(key, 'x.x.x')
                    };
                });
            } else {
                Object.entries(root.ServiceRegistry || {}).forEach(([key, value]) => {
                    services[key] = {
                        isStarted: value.isStarted,
                        version: value.version
                    };
                });
            }

            Sentry.setContext('sd-services', {
                'fs-url': fsUrl,
                ...services,
                message: contextMsg
            });

            if (fsUrl) {
                this.info({
                    message: 'Setting FullStory URL to Sentry',
                    triggerType: TriggerType.other,
                    payload: {
                        context: {
                            url: fsUrl
                        }
                    }
                });
                Sentry.setTag('FS', 'true');
            }
            if (data instanceof Error || data instanceof Event) {
                Sentry.captureException(data);
            } else {
                Sentry.captureMessage(data);
            }
        } else {
            console.warn('Logging failed because Sentry is not available');
            if (data instanceof Error) {
                console.warn(
                    JSON.stringify(data, ["message", "arguments", "type", "name"])
                );
            } else {
                console.warn(data);
            }
        }
    };

    // @ts-ignore
    private async sendLog(logdata: ILogData, level: string) {
        try {
            const { triggerType, payload = {}, message } = logdata;

            if (message instanceof Error) {
                return this.sendToSentry(message);
            } else if (typeof message !== 'string' && message.error instanceof Error) {
                return this.sendToSentry(message.error);
            } else if (payload.error instanceof Error) {
                const msg = typeof message === 'string' ? message : JSON.stringify(message);

                return this.sendToSentry(payload.error, msg);
            }

            const { siteUrl, serviceRegistry: _, context = {}, ...restPayload } = payload;

            // @ts-ignore
            const { serviceRegistry: __, ...restContext } = context;

            if (
                (typeof message === 'string' && message.includes('Script error'))
                // @ts-ignore
                || message?.message?.includes('Script error')) {
                return ;
            }

            if (message instanceof Event && message.type === 'error') {
                return this.sendToSentry(message, 'Event error');
            }

            // eslint-disable-next-line prefer-object-spread
            const _payload = Object.assign({},
                Object.keys(restPayload).length > 0 ? restPayload : {},
                Object.keys(restContext).length > 0 ? { c: { ...restContext } } : {}
            );

            // eslint-disable-next-line prefer-object-spread
            const data = Object.assign({
                msg: JSON.stringify(message),
                type: triggerType
            }, Object.keys(_payload).length > 0 ? { p: _payload } : {});

            if (message instanceof Event) {
                if (Object.keys(JSON.parse(data.msg)).length <= 1) {
                    // This is a workaround for the case when the message is an event and the event has no data
                    return ;
                }
            }

            const sentryData = JSON.stringify(data);


            this.sendToSentry(sentryData, 'unknown error');
        } catch (e) {
            if (this.errorDebugger) {
                this.errorDebugger(
                    this.enhanceLogData({
                        message: 'Could not send log',
                        triggerType: TriggerType.api,
                        payload: {
                            error: e,
                            context: logdata
                        }
                    })
                );
            }
        }
    }
}
